import React, { useRef, useEffect, useState } from "react";
import { FaChartBar, FaDatabase, FaShieldAlt, FaUserTie, FaRobot, FaBrain } from "react-icons/fa";
import { animated } from 'react-spring';

// Color palette
const palette = {
  highlight: "#f6f6ff", // animations
  button: "#f6f6ff",   // buttons
  base: "#f8f8f8",     // base
  text: "#0c0c16",     // main text
  cardBg: "#fffff",
  cardShadow: "rgba(64,46,122,0.08)",
  subtitle: "#702fff",  // subtitle highlight
};

const services = [
  {
    title: "Dashboards & Reporting",
    description:
      "Transform the data from your tools and systems into actionable insights with custom dashboards and real-time reporting.",
    icon: <FaChartBar size={36} color={palette.highlight} />,
  },
  {
    title: "AI Data Lakehouse Platform",
    description:
      "Leverage a secure, scalable AI-enabled data lakehouse for instant analytics and business value—no infrastructure hassle.",
    icon: <FaDatabase size={36} color={palette.button} />,
  },
  {
    title: "Governance Portal",
    description:
      "Centralize data product management, access, and compliance with seamless integration to major platforms.",
    icon: <FaShieldAlt size={36} color={palette.highlight} />,
  },
  {
    title: "Consulting & Strategy",
    description:
      "Expert guidance to identify use cases, define data strategy, and implement solutions tailored to your business goals.",
    icon: <FaUserTie size={36} color={palette.highlight} />,
  },
  {
    title: "AI-Powered Data Discovery",
    description:
      "Uncover hidden patterns and trends in your data using advanced AI algorithms. Empower your teams to make smarter, faster decisions with automated insights and anomaly detection.",
    icon: <FaRobot size={36} color={palette.button} />,
  },
  {
    title: "Natural Language Analytics",
    description:
      "Ask questions about your data in plain English and get instant, actionable answers. Our AI-driven analytics platform bridges the gap between business users and complex datasets.",
    icon: <FaBrain size={36} color={palette.highlight} />,
  },
];

const ServicesOverview = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showCards, setShowCards] = useState(false);

  // Intersection Observer for scroll-in animation
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Staggered appearance
  useEffect(() => {
    let subtitleTimeout, titleTimeout, descTimeout, cardsTimeout;
    if (visible) {
      subtitleTimeout = setTimeout(() => setShowSubtitle(true), 200);
      titleTimeout = setTimeout(() => setShowTitle(true), 600);
      descTimeout = setTimeout(() => setShowDesc(true), 800); // appears right after title
      cardsTimeout = setTimeout(() => setShowCards(true), 800); // cards slide in right after title
    }
    return () => {
      clearTimeout(subtitleTimeout);
      clearTimeout(titleTimeout);
      clearTimeout(descTimeout);
      clearTimeout(cardsTimeout);
    };
  }, [visible]);

  // Card animation state for all cards
  const [cardVisibles, setCardVisibles] = useState(Array(services.length).fill(false));
  useEffect(() => {
    if (showCards) {
      services.forEach((_, idx) => {
        setTimeout(() => {
          setCardVisibles(prev => {
            const updated = [...prev];
            updated[idx] = true;
            return updated;
          });
        }, idx * 180);
      });
    } else {
      setCardVisibles(Array(services.length).fill(false));
    }
  }, [showCards]);

  // Responsive styles for mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;

  return (
    <section ref={sectionRef} className="services-overview-section" style={{ ...styles.section, padding: isMobile ? '32px 0 0 0' : styles.section.padding }}>
      <div style={styles.headerWrap}>
        <span
          style={{
            ...styles.subtitle,
            fontSize: isMobile ? 13 : styles.subtitle.fontSize,
            marginBottom: isMobile ? 8 : styles.subtitle.marginBottom,
            opacity: showSubtitle ? 1 : 0,
            transform: showSubtitle ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(.4,2,.6,1), transform 0.7s cubic-bezier(.4,2,.6,1)'
          }}
        > Our Services</span>
        <h1
          style={{
            ...styles.title,
            fontSize: isMobile ? 5 : styles.title.fontSize,
            margin: isMobile ? '0 0 10px 0' : styles.title.margin,
            opacity: showTitle ? 1 : 0,
            transition: 'opacity 0.7s 0.2s cubic-bezier(.4,2,.6,1)',
            textAlign: 'center' // Ensure centered alignment
          }}
        >
            Turn Your Data into Decisions — Instantly
        </h1>
        <p
          style={{
            ...styles.desc,
            fontSize: isMobile ? 13 : styles.desc.fontSize,
            margin: isMobile ? '0 0 24px 0' : styles.desc.margin,
            opacity: showDesc ? 1 : 0,
            transition: 'opacity 0.7s 0.4s cubic-bezier(.4,2,.6,1)'
          }}
        >
            Our plug-and-play data platform transforms your CRM, Sales, Marketing, and Financial data into trusted, actionable insights — from day one.
            Built-in quality, proven best practices, and fast time-to-value. Let your team focus on growth.
        </p>
      </div>
      <div style={{
        ...styles.cardsWrap,
        gridTemplateColumns: isMobile ? '1fr' : styles.cardsWrap.gridTemplateColumns,
        gap: isMobile ? 20 : styles.cardsWrap.gap,
        marginTop: isMobile ? 24 : styles.cardsWrap.marginTop,
        maxWidth: isMobile ? 400 : styles.cardsWrap.maxWidth,
      }}>
        {services.map((service, idx) => (
          <div
            key={idx}
            className="service-card"
            style={{
              ...styles.card,
              padding: isMobile ? '20px 8px 16px 8px' : styles.card.padding,
              minHeight: isMobile ? 140 : styles.card.minHeight,
              alignItems: isMobile ? 'center' : styles.card.alignItems,
              opacity: cardVisibles[idx] ? 1 : 0,
              transform: cardVisibles[idx] ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.9s cubic-bezier(.4,2,.6,1), transform 0.9s cubic-bezier(.4,2,.6,1)'
            }}
          >
            <div style={{
              ...styles.iconWrap,
              padding: isMobile ? 8 : styles.iconWrap.padding,
              marginBottom: isMobile ? 10 : styles.iconWrap.marginBottom,
            }}>
              {React.cloneElement(service.icon, { size: isMobile ? 22 : 36 })}
            </div>
            <h3 style={{
              ...styles.cardTitle,
              fontSize: isMobile ? 13.5 : styles.cardTitle.fontSize,
              margin: isMobile ? '0 0 6px 0' : styles.cardTitle.margin,
              textAlign: isMobile ? 'center' : styles.cardTitle.textAlign,
            }}>{service.title}</h3>
            <p style={{
              ...styles.cardDesc,
              fontSize: isMobile ? 11.5 : styles.cardDesc.fontSize,
              textAlign: isMobile ? 'center' : styles.cardDesc.textAlign,
            }}>{service.description}</p>
          </div>
        ))}
      </div>
      {/* Animation styles */}
      <style>{`
        .service-card:hover {
          transform: translateY(-8px) scale(1.04) rotate(-1deg) !important;
          box-shadow: 0 8px 32px 0 rgba(64,46,122,0.18);
          transition: transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);
        }
        @media (max-width: 600px) {
          .service-card { min-height: 160px; }
        }
      `}</style>
    </section>
  );
};

const styles = {
  section: {
    background: palette.base,
    padding: "64px 0 48px 0",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "100px",
  },
  headerWrap: {
    maxWidth: 900,
    textAlign: "center",
    marginBottom: 40,
  },
  subtitle: {
    color: palette.subtitle,
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    marginBottom: 12,
    display: "inline-block",
    fontFamily: 'Inter, sans-serif',
  },
  title: {
    fontSize: 36,
    fontWeight: 400, // match HeroSection
    color: palette.text,
    margin: "0 0 18px 0",
    lineHeight: 1.15,
    fontFamily: 'Inter, sans-serif',
    textAlign: 'center', // Ensure centered in base styles
  },
  desc: {
    color: "#666",
    fontSize: 18, // match HeroSection descriptionStyles for desktop
    fontWeight: 300, // match HeroSection
    margin: "0 auto 0 auto",
    maxWidth: 800,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.6,
    textAlign: 'center',
  },
  cardsWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 32,
    width: "100%",
    maxWidth: 1100,
    marginTop: 32,
  },
  card: {
    background: palette.cardBg,
    borderRadius: 18,
    boxShadow: `0 4px 24px 0 ${palette.cardShadow}`,
    padding: "32px 24px 28px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minHeight: 220,
    transition: "transform 0.2s cubic-bezier(.4,2,.6,1), box-shadow 0.2s cubic-bezier(.4,2,.6,1)",
    cursor: "pointer",
    fontFamily: 'Inter, sans-serif',
  },
  iconWrap: {
    marginBottom: 18,
    background: 'linear-gradient(135deg, #b8baff 0%, #6666ff 50%, #7bd7f7 100%)',
    borderRadius: "50%",
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(64,46,122,0.10)",
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: palette.text,
    margin: "0 0 10px 0",
    fontFamily: 'Inter, sans-serif',
  },
  cardDesc: {
    color: "#666",
    fontSize: 16,
    fontWeight: 300, // match HeroSection
    margin: 0,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
  },
  // Responsive styles
  '@media (max-width: 600px)': {
    title: {
      fontSize: 24,
    },
    desc: {
      fontSize: 16,
    },
    cardsWrap: {
      gridTemplateColumns: '1fr',
      gap: 20,
    },
    card: {
      padding: '24px 12px 20px 12px',
      minHeight: 180,
    },
    iconWrap: {
      padding: 8,
    },
    cardTitle: {
      fontSize: 16,
    },
    cardDesc: {
      fontSize: 14,
    },
  },
};

export default ServicesOverview;
